// Hörerchat: den Raum aus der Suite heraus begleiten.
//
// Was hier anders ist als auf der Hörerseite: Die Suite sitzt mit dem eigenen
// Zugang im Raum – mit Namen aus dem Zugang, als Team erkennbar, ohne Drossel.
// Dazu die zwei Griffe, die eine Moderation wirklich braucht: eine Zeile
// wegnehmen und jemanden hinausbitten (im Notfall mit Sperre).
//
// „Unsichtbar" gibt es mit Absicht: Wer nur mitliest, soll den Raum nicht
// verändern. Ein Raum, in dem sichtbar „die Leitung" sitzt, ist ein anderer
// Raum.
//
// Sichtbar ist die Seite nur, wenn das Modul „Hörerchat" eingeschaltet ist
// (Aussehen → Mitmach-Module).

import { useCallback, useEffect, useRef, useState } from "react";

import { api, chatApi, type ChatEntry, type ChatPerson } from "../api";
import { useAuth } from "../auth/AuthProvider";
import { errorText } from "../components/streaming-ui";
import { Initialen, KanalWahl, Meldungen, ModulAus, Schalter, Sym } from "../components/social-ui";
import { Badge, Callout, EmptyState, Page, PageHeader } from "../components/ui";
import type { Channel } from "../types";

const clock = (iso: string): string => new Date(iso).toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" });

export function ChatPage(): JSX.Element {
  const { user } = useAuth();
  const mayRun = user?.role === "moderator" || user?.role === "tenant_admin" || user?.role === "super_admin";

  const [channels, setChannels] = useState<Channel[]>([]);
  const [slug, setSlug] = useState("");
  const [chatOn, setChatOn] = useState<boolean | null>(null);
  const [entries, setEntries] = useState<ChatEntry[]>([]);
  const [people, setPeople] = useState<ChatPerson[]>([]);
  const [open, setOpen] = useState(true);
  const [invisible, setInvisible] = useState(true);
  const [connected, setConnected] = useState(false);
  const [text, setText] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const ws = useRef<WebSocket | null>(null);
  const list = useRef<HTMLDivElement>(null);

  useEffect(() => {
    api.listChannels().then((cs) => {
      setChannels(cs);
      setSlug((s) => s || cs[0]?.slug || "");
    }).catch((e) => setError(errorText(e)));
    api.listModules().then((m) => setChatOn(m.some((x) => x.id === "chat" && x.enabled))).catch(() => setChatOn(false));
  }, []);

  /** Verbinden – erst den Schlüssel holen, dann die Steckdose aufmachen. */
  const connect = useCallback(async (channelSlug: string, unsichtbar: boolean) => {
    ws.current?.close();
    ws.current = null;
    setConnected(false);
    if (!channelSlug) return;
    try {
      const stand = await chatApi.state(channelSlug);
      setEntries(stand.entries);
      setPeople(stand.online);
      setOpen(stand.open);
      const { token } = await chatApi.token(channelSlug);
      const proto = window.location.protocol === "https:" ? "wss:" : "ws:";
      const url = `${proto}//${window.location.host}/api/chat/ws?token=${encodeURIComponent(token)}${unsichtbar ? "&unsichtbar=1" : ""}`;
      const socket = new WebSocket(url);
      ws.current = socket;
      socket.onopen = () => setConnected(true);
      socket.onclose = () => setConnected(false);
      socket.onmessage = (ev) => {
        let m: Record<string, unknown>;
        try {
          m = JSON.parse(String(ev.data)) as Record<string, unknown>;
        } catch {
          return;
        }
        const typ = m.typ as string;
        if (typ === "willkommen") {
          setEntries((m.entries as ChatEntry[]) ?? []);
          setPeople((m.leute as ChatPerson[]) ?? []);
        } else if (typ === "zeile") {
          setEntries((e) => [...e, m as unknown as ChatEntry].slice(-300));
        } else if (typ === "da") {
          setPeople((m.leute as ChatPerson[]) ?? []);
        } else if (typ === "weg") {
          setEntries((e) => e.filter((z) => z.id !== m.id));
        } else if (typ === "fluester") {
          setEntries((e) => [...e, { id: `f${Date.now()}`, at: String(m.at), nick: String(m.von), rang: "gast", kind: "system", text: `(nur an ${String(m.an)}) ${String(m.text)}` }]);
        } else if (typ === "fehler") {
          setError(String(m.text));
        }
      };
    } catch (e) {
      setError(errorText(e));
    }
  }, []);

  useEffect(() => {
    if (!slug || !chatOn || !mayRun) return;
    void connect(slug, invisible);
    return () => {
      ws.current?.close();
      ws.current = null;
    };
  }, [slug, chatOn, mayRun, invisible, connect]);

  // Beim Nachrücken unten bleiben – aber nur, wenn man ohnehin unten war.
  useEffect(() => {
    const el = list.current;
    if (!el) return;
    if (el.scrollHeight - el.scrollTop - el.clientHeight < 120) el.scrollTop = el.scrollHeight;
  }, [entries]);

  const send = (): void => {
    const value = text.trim();
    if (!value || ws.current?.readyState !== WebSocket.OPEN) return;
    ws.current.send(JSON.stringify({ typ: "sag", text: value }));
    setText("");
    setError(null);
  };

  const remove = (id: string): void => {
    ws.current?.send(JSON.stringify({ typ: "loeschen", id }));
  };

  const kick = (person: ChatPerson, sperren: boolean): void => {
    if (sperren && !window.confirm(`„${person.nick}" für 60 Minuten sperren? Von diesem Gerät kommt so lange nichts mehr.`)) return;
    ws.current?.send(JSON.stringify({ typ: "raus", teilnehmer: person.id, sperren, minuten: 60 }));
    setNotice(sperren ? `„${person.nick}" ist gesperrt.` : `„${person.nick}" wurde hinausgebeten.`);
  };

  if (!mayRun) {
    return (
      <Page>
        <PageHeader eyebrow="Hörer" title="Hörerchat" />
        <div className="card p-2">
          <EmptyState icon={<Sym name="chat" className="h-5 w-5" />} title="Den Chat begleitet die Moderation." />
        </div>
      </Page>
    );
  }
  if (chatOn === false) {
    return (
      <ModulAus title="Hörerchat" icon="chat">
        Das Modul „Hörerchat“ ist aus. Einschalten geht unter Aussehen → Mitmach-Module; dort stehen auch Regeln,
        Begrüßung und wie lange der Verlauf bleibt.
      </ModulAus>
    );
  }

  const gaeste = people.filter((p) => p.rang === "gast").length;

  return (
    <Page className="max-w-6xl">
      <PageHeader
        eyebrow="Hörer"
        title="Hörerchat"
        description={<>
          Der Raum des Kanals, so wie die Hörer ihn sehen. Du schreibst als <strong className="font-semibold text-primary">{user?.display_name ?? "Team"}</strong> und
          bist als Team erkennbar. Die Einstellungen (Regeln, Begrüßung, Abstand, Aufbewahrung) stehen unter Aussehen → Mitmach-Module.
        </>}
        actions={<KanalWahl channels={channels} slug={slug} onChange={setSlug} />}
      />

      <div className="space-y-2">
        {!open && (
          <Callout tone="warning" className="mb-4">
            Der Raum ist für Hörer gerade zu („nur während einer Live-Sendung“). Mitlesen und schreiben kannst du trotzdem.
          </Callout>
        )}
      </div>
      <Meldungen error={error} notice={notice} onClear={() => setNotice(null)} />

      <div className="grid gap-5 md:grid-cols-[minmax(0,1fr)_16rem]">
        <section className="card flex min-w-0 flex-col overflow-hidden">
          {/* Kopf des Raums: Verbindung und „unsichtbar“ */}
          <div className="flex flex-wrap items-center justify-between gap-3 border-b border-default px-4 py-3">
            <div className="flex items-center gap-2.5">
              <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-brand-primary/10 text-brand-primary"><Sym name="chat" className="h-4 w-4" /></span>
              <div>
                <div className="text-sm font-semibold text-primary">{channels.find((c) => c.slug === slug)?.name ?? "Raum"}</div>
                <div className="text-xs text-muted">{people.length} im Raum{gaeste !== people.length ? ` · ${gaeste} Hörer` : ""}</div>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <label className="flex items-center gap-2 text-sm text-secondary">
                <Schalter checked={invisible} onChange={setInvisible} label="Unsichtbar mitlesen" />
                <span className="flex items-center gap-1"><Sym name="eyeOff" className="h-3.5 w-3.5 text-muted" />Unsichtbar mitlesen</span>
              </label>
              <Badge tone={connected ? "success" : "neutral"} dot className="whitespace-nowrap">{connected ? "verbunden" : "nicht verbunden"}</Badge>
            </div>
          </div>

          <div ref={list} className="h-[28rem] overflow-y-auto bg-surface-sunken/40 px-2 py-3 sm:px-3">
            {entries.length === 0 ? (
              <div className="flex h-full flex-col items-center justify-center text-center text-sm text-muted">
                <Sym name="chat" className="mb-2 h-6 w-6 opacity-60" />
                Noch nichts geschrieben.
              </div>
            ) : (
              <ul className="space-y-0.5">
                {entries.map((z, i) => {
                  const vorher = entries[i - 1];
                  // Mehrere Zeilen derselben Person hintereinander: Name und Bild nur einmal.
                  const folge = z.kind === "text" && vorher?.kind === "text" && vorher.nick === z.nick;
                  const team = z.rang !== "gast";
                  if (z.kind !== "text") {
                    return (
                      <li key={z.id} className="group flex items-center justify-center gap-2 px-2 py-1 text-center text-xs italic text-muted">
                        <span className="font-mono not-italic">{clock(z.at)}</span>
                        <span className="min-w-0 whitespace-pre-line break-words">{z.kind === "aktion" ? `${z.nick} ${z.text}` : z.text}</span>
                        {z.kind !== "system" && !String(z.id).startsWith("f") && (
                          <button onClick={() => remove(z.id)} title="Zeile wegnehmen" aria-label="Zeile wegnehmen"
                            className="rounded p-0.5 not-italic opacity-0 hover:text-red-600 focus:opacity-100 group-hover:opacity-100 max-md:opacity-100" style={{ minHeight: 0 }}><Sym name="x" className="h-3 w-3" /></button>
                        )}
                      </li>
                    );
                  }
                  return (
                    <li key={z.id} className={`group flex items-start gap-2.5 rounded-lg px-2 py-1 hover:bg-surface-hover ${folge ? "" : "mt-2 first:mt-0"}`}>
                      <span className="w-8 flex-shrink-0">
                        {!folge && (team
                          ? <span className="flex h-8 w-8 items-center justify-center rounded-full bg-brand-primary text-white"><Sym name="radio" className="h-4 w-4" /></span>
                          : <Initialen name={z.nick} className="h-8 w-8 text-xs" />)}
                      </span>
                      <div className="min-w-0 flex-1">
                        {!folge && (
                          <div className="flex items-baseline gap-2">
                            <span className={`text-sm font-semibold ${team ? "text-brand-primary" : "text-primary"}`}>{z.nick}</span>
                            {team && <Badge tone="brand" className="!py-0 !text-[0.65rem]">Team</Badge>}
                            <span className="font-mono text-[11px] text-muted">{clock(z.at)}</span>
                          </div>
                        )}
                        <p className="whitespace-pre-line break-words text-sm leading-relaxed text-primary">{z.text}</p>
                      </div>
                      {folge && <span className="mt-0.5 hidden font-mono text-[11px] text-muted group-hover:inline">{clock(z.at)}</span>}
                      {!String(z.id).startsWith("f") && (
                        <button onClick={() => remove(z.id)} title="Zeile wegnehmen" aria-label="Zeile wegnehmen"
                          className="btn btn-ghost btn-sm btn-icon h-7 w-7 flex-shrink-0 opacity-0 hover:!text-red-600 focus:opacity-100 group-hover:opacity-100 max-md:opacity-100"><Sym name="trash" className="h-3.5 w-3.5" /></button>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
          <div className="flex gap-2 border-t border-default p-3">
            <input
              value={text}
              onChange={(e) => setText(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); send(); } }}
              maxLength={500}
              placeholder={connected ? "Schreib etwas …" : "Nicht verbunden"}
              disabled={!connected}
              className="input min-w-0 flex-1"
            />
            <button onClick={send} disabled={!connected || !text.trim()} className="btn btn-primary">
              <Sym name="send" className="h-3.5 w-3.5" />Senden
            </button>
          </div>
        </section>

        <aside className="card flex min-w-0 flex-col">
          <div className="card-header">
            <h2 className="card-title">Wer ist da</h2>
            <Badge>{people.length}</Badge>
          </div>
          <ul className="flex-1 space-y-0.5 p-2 text-sm">
            {people.length === 0 && <li className="px-2 py-3 text-muted">Niemand.</li>}
            {people.map((p) => (
              <li key={p.id} className="group flex items-center gap-2 rounded-lg px-2 py-1.5 hover:bg-surface-hover">
                <span className="relative">
                  {p.rang === "gast"
                    ? <Initialen name={p.nick} className="h-7 w-7 text-[0.7rem]" />
                    : <span className="flex h-7 w-7 items-center justify-center rounded-full bg-brand-primary text-white"><Sym name="radio" className="h-3.5 w-3.5" /></span>}
                  <span className="absolute -bottom-0.5 -right-0.5 h-2.5 w-2.5 rounded-full bg-emerald-500 ring-2 ring-[var(--bg-card)]" />
                </span>
                <span className={`min-w-0 flex-1 truncate ${p.rang === "gast" ? "text-primary" : "font-semibold text-brand-primary"}`}>{p.nick}</span>
                {p.rang === "gast" && (
                  <span className="flex gap-0.5 opacity-0 focus-within:opacity-100 group-hover:opacity-100 max-md:opacity-100">
                    <button onClick={() => kick(p, false)} title="hinausbitten"
                      className="btn btn-ghost btn-sm h-7 px-2">raus</button>
                    <button onClick={() => kick(p, true)} title="für 60 Minuten sperren"
                      className="btn btn-ghost btn-sm h-7 px-2 !text-red-600 dark:!text-red-400">sperren</button>
                  </span>
                )}
              </li>
            ))}
          </ul>
          <p className="border-t border-default px-4 py-3 text-[11px] leading-relaxed text-muted">
            Eine Sperre gilt für das Gerät und läuft von selbst ab – spätestens um Mitternacht, weil der Erkennungswert
            dann ohnehin ein anderer ist.
          </p>
        </aside>
      </div>

      <p className="mt-4 text-xs text-muted">
        Im Studio steht derselbe Raum im Fach <em>Chat</em> – dort liest man ihn während der Sendung, ohne die Suite zu öffnen.
        Tastenkürzel im Chat: <code className="rounded bg-input px-1 py-0.5 text-[11px]">/me</code> für eine Handlung, <code className="rounded bg-input px-1 py-0.5 text-[11px]">/w Name Text</code> zum Flüstern.
      </p>
    </Page>
  );
}
